import React, { useState } from 'react';
import { Mail, Send, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { MesoamericanBackground, PyramidDivider } from './MesoamericanPatterns';
import { useLanguage } from '../context/LanguageContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Newsletter = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { t } = useLanguage();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      await axios.post(`${BACKEND_URL}/api/newsletter/subscribe`, { email });
      setIsSubscribed(true);
      setEmail('');
      toast.success(t('newsletter.success'));
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('This email is already subscribed.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
      setTimeout(() => setIsSubscribed(false), 3000);
    }
  };

  return (
    <section className="relative py-24 overflow-hidden bg-teal-600">
      <MesoamericanBackground opacity={0.08} />
      
      <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <PyramidDivider className="mb-8 opacity-30" />
        
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium mb-6">
          <Mail size={16} />
          <span>{t('newsletter.title')}</span>
        </div>
        
        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4">
          {t('newsletter.title').split(' ')[0]} <span className="text-amber-200">{t('newsletter.title').split(' ')[1]}</span>
        </h2>
        
        <p className="text-lg text-white/90 mb-10 max-w-2xl mx-auto">
          {t('newsletter.subtitle')}
        </p>
        
        <form onSubmit={handleSubmit} className="max-w-xl mx-auto" data-testid="newsletter-form">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-grow">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400" size={20} />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newsletter.placeholder')}
                required
                data-testid="newsletter-email-input"
                className="w-full pl-12 pr-4 py-4 bg-white border border-stone-200 rounded-lg text-stone-800 placeholder:text-stone-400 focus:outline-none focus:border-teal-300 focus:ring-2 focus:ring-white/30 transition-all duration-300"
              />
            </div>
            <button
              type="submit"
              disabled={isSubmitting || isSubscribed}
              data-testid="newsletter-submit-btn"
              className="px-8 py-4 bg-amber-500 hover:bg-amber-400 disabled:bg-stone-300 text-white disabled:text-stone-500 font-semibold rounded-lg transition-all duration-300 hover:shadow-lg hover:shadow-amber-500/25 flex items-center justify-center gap-2 min-w-[160px]"
            >
              {isSubmitting ? (
                <span className="animate-spin">⏳</span>
              ) : isSubscribed ? (
                <>
                  <CheckCircle size={20} />
                  {t('newsletter.success').substring(0, 12)}
                </>
              ) : (
                <>
                  {t('newsletter.subscribe')}
                  <Send size={18} />
                </>
              )}
            </button>
          </div>
        </form>
        
        <p className="text-white/70 text-sm mt-6">
          {t('newsletter.privacy')}
        </p>
        
        <PyramidDivider className="mt-12 opacity-30" />
      </div>
    </section>
  );
};

export default Newsletter;
